import { useCallback, useEffect, useMemo } from 'react';
import { useForceRender } from 'hooks/useForceRender';
import PublishModel from 'models/PublishModel';

export type PublishRunState = 'idle' | 'publishing' | 'done' | 'failed';

export type PublishSummary = {
  state: PublishRunState;
  doneResources: number;
  totalResources: number;
  failedResources: number;
  // Resource currently exporting, when publishing.
  currentName?: string;
  currentDone?: number;
  currentTotal?: number;
  // 0..100 across resources, for the compact bars.
  percent: number;
};

export type UseCoursePublishResult = {
  model: PublishModel;
  summary: PublishSummary;
  publish: () => Promise<void>;
};

// One PublishModel per course, shared between the collapsed panel summary and the
// expanded publish section — both must read the SAME run.
const useCoursePublish = (courseId: number): UseCoursePublishResult => {
  const forceRender = useForceRender();
  const model = useMemo(() => new PublishModel({ render: forceRender, courseId }), [forceRender, courseId]);

  useEffect(() => {
    void model.getResources();
  }, [model]);

  const publish = useCallback(() => model.publish(), [model]);

  const resources = model.resources;
  const totalResources = resources.length;
  const doneResources = resources.filter((resource) => resource.completed).length;
  const failedResources = resources.filter((resource) => resource.failed).length;
  const current = resources.find((resource) => resource.publishing);

  let state: PublishRunState = 'idle';
  if (model.publishing) {
    state = 'publishing';
  } else if (failedResources > 0) {
    state = 'failed';
  } else if (totalResources > 0 && doneResources + failedResources === totalResources) {
    state = 'done';
  }

  // Whole-run progress: completed resources plus the in-flight resource's own fraction.
  let progressUnits = doneResources + failedResources;
  if (current && current.batch !== 0 && current.items.length > 0) {
    progressUnits += current.done.length / current.items.length;
  }
  const percent = totalResources === 0 ? 0 : Math.min(100, (progressUnits * 100) / totalResources);

  return {
    model,
    publish,
    summary: {
      state,
      doneResources,
      totalResources,
      failedResources,
      currentName: current?.name,
      currentDone: current && current.batch !== 0 ? current.done.length : undefined,
      currentTotal: current && current.batch !== 0 ? current.items.length : undefined,
      percent,
    },
  };
};

export default useCoursePublish;
